package org.matthewo.pages

import emotion.react.css
import io.ktor.client.call.*
import io.ktor.client.plugins.resources.*
import js.coroutines.awaitCancellation
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.launch
import org.matthewo.*
import org.matthewo.api.Search
import org.matthewo.api.SearchEntry
import org.matthewo.config.CONFIG
import org.matthewo.hooks.useCoroutineScope
import org.matthewo.pages.event.toEvent
import org.matthewo.pages.team.tabs.toTeam
import org.matthewo.routes.API
import org.matthewo.styles.AUTO_COLOR
import org.matthewo.styles.BACKGROUND_COLOR
import org.matthewo.styles.ENDGAME_COLOR
import org.matthewo.styles.EVENT_DATE_FORMAT
import org.matthewo.styles.FONT_COLOR
import org.matthewo.styles.TELEOP_COLOR
import org.matthewo.styles.TITLE_FONT_COLOR
import org.matthewo.styles.TOTAL_COLOR
import org.matthewo.styles.transitions
import react.*
import react.dom.html.ReactHTML.b
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.h3
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.li
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.ul
import tanstack.react.router.useNavigate
import tanstack.react.router.useParams
import web.cssom.*
import web.cssom.atrule.maxWidth
import web.dom.document
import web.events.*
import web.html.HTMLInputElement
import web.keyboard.Enter
import web.keyboard.KeyCode
import web.keyboard.KeyboardEvent
import web.window.Window
import web.window.window
import kotlin.js.Date

private const val NUM_SEARCH_RESULTS = 20

// start is an ISO date (yyyy-MM-dd); built from its parts so it stays the same day in every time zone
private fun formatStartDate(start: String): String {
    val (year, month, day) = start.split("-").map { it.toInt() }
    return Date(year, month - 1, day).toLocaleDateString(options = EVENT_DATE_FORMAT)
}

private val Search.ActiveEvent.Stage.color
    get() = when (this) {
        Search.ActiveEvent.Stage.NOT_STARTED -> NamedColor.gray
        Search.ActiveEvent.Stage.SCHEDULED -> TELEOP_COLOR.chart
        Search.ActiveEvent.Stage.QUALS -> AUTO_COLOR.chart
        Search.ActiveEvent.Stage.ALLIANCE_SELECTION -> TOTAL_COLOR.chart
        Search.ActiveEvent.Stage.PLAYOFFS -> ENDGAME_COLOR.chart
        Search.ActiveEvent.Stage.AWARDS, Search.ActiveEvent.Stage.COMPLETE -> TITLE_FONT_COLOR
    }

val Search = FC("Search") {
    useDefaultHeader()

    val coroutineScope = useCoroutineScope()
    val navigate = useNavigate()
    val pathParams = useParams()

    var entries by useState(listOf<SearchEntry>())
    var activeEvents by useState<List<Search.ActiveEvent>>()
    var query by useState<String?>(null)
    var results by useState(listOf<Pair<SearchEntry, SearchMatch?>>())

    var searchRef = useRef<HTMLInputElement>()

    useLoading(null == activeEvents)

    useEffectOnce {
        val type = EventType<Event>("keydown")
        val handler: EventHandler<Event, Window, EventTarget> =
            EventHandler { event: KeyboardEvent ->
                if ("/" == event.key && searchRef.current != document.activeElement) {
                    event.preventDefault()
                    searchRef.current?.focus()
                }
            }

        window.addEventListener(type, handler)
        awaitCancellation { window.removeEventListener(type, handler) }
    }

    useEffect(pathParams) {
        val job = coroutineScope.launch {
            val search = client.get(API.Search()).body<Search>()
            entries = search.entries
            activeEvents = search.activeEvents
        }
        awaitCancellation { job.cancel() }
    }
    fun SearchEntry.term() = when (this) {
        is SearchEntry.Event -> name
        is SearchEntry.Team -> name
    }

    val searcher = useMemo(entries) {
        Searcher(entries.sortedByDescending { (it as? SearchEntry.Event)?.year ?: 0 }) { it.term() }
    }
    useEffect(searcher, query) {
        val job = coroutineScope.launch {
            val text = query
            if (null == text) {
                results = []
            } else {
                val number = text.toIntOrNull() != null
                results = if (number) {
                    entries
                        .map { entry ->
                            ensureActive()
                            entry to when (entry) {
                                is SearchEntry.Team -> {
                                    val numberString = entry.number.toString()
                                    if (numberString.startsWith(text)) text.length / numberString.length.toDouble()
                                    else 0.0
                                }

                                is SearchEntry.Event -> 0.0
                            }
                        }
                        .sortedByDescending { it.second }
                        .take(NUM_SEARCH_RESULTS)
                        .map { it.first to null }
                } else {
                    searcher.searchCancelable(text).take(NUM_SEARCH_RESULTS)
                }
            }
        }
        awaitCancellation {
            job.cancel()
        }
    }

    fun SearchEntry.select() = navigate {
        when (this@select) {
            is SearchEntry.Team -> toTeam(CONFIG.CURRENT_YEAR, number)
            is SearchEntry.Event -> toEvent(year, code)
        }
    }

    div {
        css {
            width = 100.pct
            flex = number(1.0)
            fontSize = 1.em
            overflow = Overflow.hidden
            display = Display.flex
            flexDirection = FlexDirection.column
        }

        input {
            css {
                fontSize = 1.rem

                width = 20.em
                padding = 1.0.em
                margin = 1.em
                background = TITLE_FONT_COLOR
                color = BACKGROUND_COLOR
                border = None.none
                borderRadius = 0.25.em
                outline = None.none

                val transitionDuration = 0.3.s
                focus {
                    transition = transitions(
                        Transition(PropertyName.width, transitionDuration, TransitionTimingFunction.ease),
                        Transition(PropertyName.padding, transitionDuration, TransitionTimingFunction.ease, transitionDuration),
                    )
                    width = 50.pct
                    padding = 1.0.em
                }

                not("focus") {
                    transition = transitions(
                        Transition(PropertyName.padding, transitionDuration, TransitionTimingFunction.ease),
                        Transition(PropertyName.width, transitionDuration, TransitionTimingFunction.ease, transitionDuration),
                    )
                }

                placeholder {
                    color = BACKGROUND_COLOR
                }
            }
            ref = searchRef
            placeholder = "Search"
            onInput = { event ->
                query = event.currentTarget.value
            }
            onKeyUp = { event ->
                if (KeyCode.Enter == event.code) results.firstOrNull()?.first?.select()
            }
        }

        div {
            css {
                width = 100.pct
                flexGrow = number(1.0)
                display = Display.flex
                gap = 1.rem
                overflow = Overflow.hidden
                flexDirection = FlexDirection.row
                `@media`(maxWidth(480.px)) {
                    flexDirection = FlexDirection.column
                }
            }

            ul {
                css {
                    flex = number(1.0)
                    overflowY = Auto.auto
                    margin = 0.em
                    paddingRight = 0.5.em
                }
                results.forEach { (entity, matches) ->
                    li {
                        key = Key(entity.hashCode().toString())
                        css {
                            padding = 0.25.em
                        }
//                        +"${matches?.cost}: "
                        +when (entity) {
                            is SearchEntry.Team -> "${entity.number} - "
                            is SearchEntry.Event -> "${entity.year} - "
                        }
                        if (null == matches) {
                            +when (entity) {
                                is SearchEntry.Team -> entity.name
                                is SearchEntry.Event -> entity.name
                            }
                        } else {
                            matches.regions().forEach { (match, text) ->
                                if (match) {
                                    b {
                                        +text
                                    }
                                } else {
                                    span {
                                        css {
                                            color = NamedColor.gray
                                        }
                                        +text
                                    }
                                }
                            }
                        }
                        onClick = {
                            entity.select()
                        }
                    }
                }
            }

            ul {
                css {
                    flex = number(1.0)
                    overflowY = Auto.auto
                    margin = 0.em
                    paddingLeft = 0.em
                    paddingRight = 0.25.em
                }
                activeEvents?.sortedBy { it.start }?.groupBy { it.start }?.forEach { (start, events) ->
                    li {
                        key = Key(start)
                        css {
                            listStyle = None.none
                        }
                        h3 {
                            css {
                                // sticks 1px high and fills that pixel so a fractional-pixel layout leaves no gap
                                // above the header for the scrolling events to show through
                                position = Position.sticky
                                top = (-1).px
                                borderTop = Border(1.px, LineStyle.solid, BACKGROUND_COLOR)
                                backgroundColor = BACKGROUND_COLOR
                                zIndex = integer(1)
                                margin = 0.em
                                padding = 0.5.em
                                color = TITLE_FONT_COLOR
                            }
                            +formatStartDate(start)
                        }
                        ul {
                            css {
                                margin = 0.em
                                padding = 0.em
                            }
                            events.sortedBy { it.event.region }.forEach { (event, _, type, stage, matchesSoon) ->
                                li {
                                    key = Key(event.year.toString() + event.code)
                                    css {
                                        display = Display.flex
                                        alignItems = AlignItems.baseline
                                        gap = 0.5.em
                                        // an event is grayed out unless matches will be played at it soon
                                        color = if (matchesSoon) FONT_COLOR else NamedColor.gray
                                        padding = 0.5.em
                                    }
                                    span {
                                        css {
                                            flexShrink = number(0.0)
                                            width = 4.em
                                            fontSize = 0.75.em
                                            color = NamedColor.gray
                                        }
                                        title = type.title
                                        +type.abbreviation
                                    }
                                    span {
                                        css {
                                            flexGrow = number(1.0)
                                            // lets a long name wrap within the row instead of widening it
                                            minWidth = 0.px
                                            overflowWrap = OverflowWrap.anywhere
                                        }
                                        +if (null != event.region) "${event.region} - ${event.name}" else event.name
                                    }
                                    span {
                                        css {
                                            flexShrink = number(0.0)
                                            // fits the longest abbreviation, 8 monospace characters
                                            width = 5.em
                                            fontSize = 0.75.em
                                            color = stage.color
                                        }
                                        title = stage.title
                                        +stage.abbreviation
                                    }
                                    onClick = {
                                        navigate {
                                            toEvent(event.year, event.code)
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
